import React, { useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardContent,
  TextField,
  Box,
  CircularProgress,
  Fade,
  InputAdornment,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { environment } from '../../../environments/environment';
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from 'react-places-autocomplete';
import { useTranslation } from 'react-i18next';
import { withGoogleMaps } from '../../providers/GoogleMapsProvider';
let searchOptions;

if (
  environment.restaurantId === 'ttJordanDev' ||
  environment.restaurantId === 'ttJordan'
) {
  searchOptions = {
    types: ['geocode'],
    componentRestrictions: { country: 'JO' },
  };
} else {
  searchOptions = {
    types: ['geocode'],
    componentRestrictions: { country: 'us' },
  };
}

const AddressPickerWithGoogle = React.forwardRef(
  ({
    initialValue,
    getValues,
    externalLoading = false,
    borderRadius = '12px',
    backgroundColor = '#fafafa',
    borderColor = '#e0e0e0',
    borderWidth = 2,
    hoverBorderColor = '#ff6b35',
  }) => {
    const [googleAddress, setGoogleAddress] = useState('');

    useEffect(() => {
      setGoogleAddress(initialValue || '');
    }, [initialValue]);

    const [showOptions, setShowOptions] = useState(false);

    // https://developers.google.com/maps/documentation/javascript/examples/places-autocomplete-addressform
    const handleSelect = async (address) => {
      setShowOptions(false);
      setGoogleAddress(address);

      let route = '';
      let country = '';
      let state = '';
      let city = '';
      let zipCode = '';
      let streetNumber = '';

      const geoCode = await geocodeByAddress(address);

      const { lat, lng } = await getLatLng(geoCode[0]);

      const placeId = geoCode[0].place_id;
      const displayName = geoCode[0].formatted_address;

      geoCode[0]['address_components'].forEach((addressComponent) => {
        const { types } = addressComponent;

        if (types.includes('country')) {
          country = addressComponent['short_name'];
        }

        if (types.includes('route')) {
          route += addressComponent['short_name'];
        }
        if (types.includes('street_number')) {
          streetNumber += ' ' + addressComponent['short_name'];
        }

        if (types.includes('postal_code')) {
          zipCode = addressComponent['short_name'];
        }
        if (types.includes('administrative_area_level_1')) {
          state = addressComponent['short_name'];
        }
        if (
          types.includes('sublocality_level_1') ||
          types.includes('administrative_area_level_3') ||
          types.includes('locality') ||
          types.includes('sublocality_level_1')
        ) {
          city = addressComponent['short_name'];
        }
      });
      getValues({
        country,
        streetNumber,
        state,
        city,
        zipCode,
        placeId,
        displayName,
        lat,
        lng,
        route,
      });
    };
    useEffect(() => {
      if (googleAddress.trim() === '') {
        getValues({});
      }
    }, [googleAddress, getValues]);
    const [t] = useTranslation();
    return (
      <PlacesAutocomplete
        searchOptions={searchOptions}
        value={googleAddress}
        onChange={(value) => {
          // Immediate state update for better mobile responsiveness
          setGoogleAddress(value);
          // Simplified condition check
          setShowOptions(Boolean(value && value.trim()));
        }}
        onSelect={handleSelect}
      >
        {({ getInputProps, loading, suggestions, getSuggestionItemProps }) => (
          <Box sx={{ position: 'relative' }}>
            <TextField
              aria-label="search-address"
              variant="outlined"
              name="googleSearchField"
              fullWidth
              // Mobile optimization attributes
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck="false"
              inputMode="text"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#ff6b35', fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (loading || externalLoading) && (
                  <InputAdornment position="end">
                    <CircularProgress size={20} sx={{ color: '#ff6b35' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: borderRadius,
                  backgroundColor: backgroundColor,
                  // Simplified transition for better mobile performance
                  transition: 'border-color 0.15s ease',
                  '& fieldset': {
                    borderColor: borderColor,
                    borderWidth: borderWidth,
                  },
                  '&:hover fieldset': {
                    borderColor: hoverBorderColor,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: hoverBorderColor,
                    // Remove expensive box-shadow on mobile for better performance
                    '@media (min-width: 768px)': {
                      boxShadow: '0 0 0 3px rgba(255, 107, 53, 0.1)',
                    },
                  },
                },
                '& .MuiInputLabel-root': {
                  color: '#666',
                  fontWeight: 500,
                  '&.Mui-focused': {
                    color: '#ff6b35',
                  },
                },
              }}
              {...getInputProps({
                placeholder: "Enter your address",
                // Optimized blur handling for mobile
                onBlur: () => {
                  // Shorter timeout for better mobile UX
                  setTimeout(() => setShowOptions(false), 100);
                },
                // Add focus handler for immediate mobile keyboard response
                onFocus: () => {
                  if (googleAddress.trim()) {
                    setShowOptions(true);
                  }
                },
              })}
            />

            <Fade in={showOptions && suggestions.length > 0}>
              <Card
                sx={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  zIndex: 1000,
                  mt: 1,
                  borderRadius: '12px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                  border: '1px solid #e0e0e0',
                  overflow: 'hidden',
                }}
              >
                <CardContent sx={{ p: 0 }}>
                  {suggestions.length === 0 ? (
                    <Box
                      sx={{
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <CircularProgress size={16} sx={{ color: '#ff6b35' }} />
                      <Typography variant="body2" color="textSecondary">
                        {t('profilee.loading')}
                      </Typography>
                    </Box>
                  ) : (
                    suggestions.map((suggestion, index) => (
                      <Box
                        {...getSuggestionItemProps(suggestion)}
                        key={index}
                        sx={{
                          p: 2,
                          cursor: 'pointer',
                          // Simplified transition for better mobile performance
                          transition: 'background-color 0.1s ease',
                          borderBottom:
                            index < suggestions.length - 1
                              ? '1px solid #f0f0f0'
                              : 'none',
                          // Use active state for mobile instead of hover
                          '&:hover, &:active': {
                            backgroundColor: '#fff3e0',
                          },
                          // Add touch target optimization for mobile
                          minHeight: 48,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Grid container alignItems="center" spacing={2}>
                          <Grid item>
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                backgroundColor: 'rgba(255, 107, 53, 0.1)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <LocationIcon
                                sx={{ color: '#ff6b35', fontSize: 16 }}
                              />
                            </Box>
                          </Grid>
                          <Grid item xs>
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: 500,
                                color: '#172B4D',
                                fontSize: '14px',
                              }}
                            >
                              {suggestion.formattedSuggestion.mainText}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#6B7280',
                                fontSize: '12px',
                                mt: 0.5,
                              }}
                            >
                              {suggestion.formattedSuggestion.secondaryText}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    ))
                  )}
                </CardContent>
              </Card>
            </Fade>
          </Box>
        )}
      </PlacesAutocomplete>
    );
  }
);

export default withGoogleMaps(AddressPickerWithGoogle);
